/**
 * Versioned localStorage persistence for the in-progress assessment wizard.
 *
 * The wizard holds all of its state in memory. Persisting it means a page
 * refresh (or, crucially, an OAuth/email sign-in round-trip that leaves the
 * SPA entirely) doesn't discard the user's answers. When an anonymous user
 * reaches "Generate report" we flip the draft to `submit-pending`; on their
 * return the wizard reads that flag and finishes the submission for them.
 *
 * The version lives in the storage KEY (`:v1`), so bumping the shape simply
 * orphans older drafts under the previous key rather than deserializing them.
 * Every function here is defensive: a corrupt or foreign value reads back as
 * `null`, and writes swallow quota errors; persistence is best-effort and must
 * never throw into the render path.
 *
 * Note: prefill File objects are intentionally NOT persisted: only the applied
 * suggestions and summary, which is all the submission needs.
 */

import type { AnswerSuggestion } from "@/lib/api";

import type { CompanyMeta, Step } from "./types";

const DRAFT_KEY = "boi:assessment-draft:v1";

export type DraftStatus = "in-progress" | "submit-pending";

export interface AssessmentDraft {
  savedAt: string;
  status: DraftStatus;
  step: Step;
  meta: CompanyMeta;
  answers: Record<string, number>;
  suggestions: Record<string, AnswerSuggestion>;
  prefillSummary: string | null;
  /**
   * The question the user was last on, so a restored wizard resumes there
   * instead of the first section intro. Additive and optional: drafts written
   * before this field are still valid (treated as "no resume point").
   */
  resumeQuestionId?: string | null;
}

function isDraft(value: unknown): value is AssessmentDraft {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const d = value as Record<string, unknown>;
  return (
    (d.status === "in-progress" || d.status === "submit-pending") &&
    typeof d.step === "string" &&
    typeof d.meta === "object" &&
    d.meta !== null &&
    typeof d.answers === "object" &&
    d.answers !== null
  );
}

/** Read the saved draft, or `null` when it is missing, unparseable, or malformed. Never throws. */
export function loadDraft(): AssessmentDraft | null {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    return isDraft(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** Persist the draft. Swallows quota / serialization errors: persistence is best-effort. */
export function saveDraft(draft: AssessmentDraft): void {
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  } catch {
    // Storage full or unavailable (private mode); losing the draft is
    // acceptable; blocking the wizard is not.
  }
}

/** Remove the saved draft (on successful submit or an explicit "Start over"). */
export function clearDraft(): void {
  try {
    localStorage.removeItem(DRAFT_KEY);
  } catch {
    // Nothing actionable if removal fails.
  }
}
